import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { DescLanguageDto } from "src/dtos/descLanguage/descLanguageDto";

/*
descLanguage 코드 규칙
DESC_~ : 문구 관련 변수(실제 텍스트)
LANG_~ : 언어별 변수(언어 계열, 옵션)
*/

/** 공통 버튼 UI용 descLanguage commentCd */
export const DESC_BTN = {
	FETCH: "BTN001",
	CREATE: "BTN002",
	DELETE: "BTN003",
	SAVE: "BTN004",
	PRINT: "BTN005",
	EXCEL: "BTN006",
	RESET: "BTN007",
	CLOSE: "BTN008",
	CLEAR: "BTN009",
	CONFIRM: "BTN010",
	CANCEL: "BTN011",
	HELP: "BTN012",
	MAXIMIZE: "BTN013",
	MINIMIZE: "BTN014",
	LOGOUT: "BTN015",
} as const;

/** 검색조건·입력 Form 라벨용 descLanguage commentCd */
export const DESC_FLD = {
	WORKPLACE: "FLD001",
	DATE: "FLD002",
	PERIOD: "FLD003",
	OPERATOR: "FlD003",
	LOT_NO: "FLD000600",
	PROCESS_NAME: "FLD001700",
	SPECIFICATION: "FLD002700",
	UNIT: "FLD002900",
	GRADE: "FLD003100",
	DEFECT_DETAILS: "FLD004200",
	BUSINESS_SITE: "FLD005400",
	PRODUCTION_QTY: "FLD005900",
	PRODUCTION_DEFECT_QTY: "FLD006000",
	PRODUCTION_RESULT_NO: "FLD006400",
	GOOD_QTY: "FLD006500",
	PRODUCTION_DATE: "FLD006600",
	REQUIRED_QTY: "FLD006700",
	ACTUAL_QTY: "FLD007100",
	INBOUND_DATE: "FLD007500",
	MATERIAL_ISSUE: "FLD007700",
	MATERIAL_CODE: "FLD007800",
	WORK_PROCESS: "FLD008100",
	WORK_START: "FLD008300",
	WORK_DATE: "FLD008400",
	WORKER: "FLD008500",
	WORK_INFO: "FLD008700",
	WORK_END: "FLD008800",
	WORK_ORDER_NO: "FLD008900",
	WORK_ORDER_REMAINING_QTY: "FLD009000",
	WORK_TEAM: "FLD009100",
	INVENTORY_QTY: "FLD009200",
	ORDER_QTY: "FLD010300",
	OUTBOUND_QTY: "FLD010800",
	OUTBOUND_WAREHOUSE: "FLD011000",
	ITEM_NAME: "FLD011200",
	ITEM_CODE: "FLD011600",
	ROW_ADD: "FLD012000",
	ROW_DELETE: "FLD011900",
	WORK_ORDER: "FLD012200",
	RESULT_COUNT: "FLD012300",
	TOTAL_PRODUCTION_QTY: "FLD012400",
	DEFECT_REASON: "FLD012500",
	DEFECT_QTY: "FLD012600",
	INSPECTION_DATE: "FLD012700",
	AUTO_LABEL_PRINT: "FLD012800",
	INSPECTOR: "FLD012900",
	APPROVER: "FLD013000",
	UNINSPECTED_QTY: "FLD013100",
	INSPECTION_STATUS: "FLD013200",
	ACCEPTED_QTY: "FLD013300",
	PACK_QTY: "FLD013400",
	INSPECTION_QTY: "FLD013500",
	SEARCH_TERM: "FLD013600",
	CODE_SEARCH: "FLD013700",
	INFORMATION: "FLD013800",
	SEQUENCE: "FLD013900",
	EMPLOYEE_NO: "FLD014000",
	EMPLOYEE_NAME: "FLD014100",
	CLOSE_STATUS: "FLD014200",
	NO_DATA: "FLD014300",
	PERSON_IN_CHARGE: "FLD014400",
	PRODUCTION_ORDER_NO: "FLD014500",
	PRODUCTION_RESULT: "FLD014600",
	LOT_ISSUE: "FLD014700",
	COUNT_DISPLAY: "FLD014800",
	INSPECTION_RECEIPT: "FLD014900",
	INSPECTION_COMPLETE: "FLD015000",
	UNINSPECTED: "FLD015100",
	DEFECT_DETAILS_REGISTRATION: "FLD015200",
	START_DATETIME: "FLD015300",
	END_TIME: "FLD015400",
	MONTH: "FLD015500",
	DAY: "FLD015600",
	HOUR: "FLD015700",
	MINUTE: "FLD015800",
	WORK_ORDER_SELECTION: "FLD015900",
	PRODUCTION_RESULT_REGISTRATION: "FLD016000",
	GENERATED_LOT_LIST: "FLD016100",
	PROCESS_INPUT_CONSUMED_MATERIAL_LIST: "FLD016200",
	CUMULATIVE_PRODUCTION_QTY: "FLD016300",
} as const;

/** toast·validation 메시지용 descLanguage commentCd */
export const DESC_MSG = {
	DATE_ERROR: "MSG001",
	PERIOD_ERROR: "MSG002",
	PRODUCTION_RESULT_ERROR: "MSG003",
} as const;

/** DescLanguageDto 다국어 텍스트 필드명 (lang01 ~ lang15) */
export type DescLangField =
	| "lang01" | "lang02" | "lang03" | "lang04" | "lang05"
	| "lang06" | "lang07" | "lang08" | "lang09" | "lang10"
	| "lang11" | "lang12" | "lang13" | "lang14" | "lang15";

/** 언어별 UI 폰트 크기 적용 대상 */
export type DescUiTarget = "sidebarMenu" | "tabTitle";

const DEFAULT_FONT_SIZE: Record<DescUiTarget, number> = {
	sidebarMenu: 18,
	tabTitle: 18,
};

/** selectedLanguageCd(minorCd)별 UI 폰트 크기(px) */
const LANG_FONT_SIZE: Record<string, Partial<Record<DescUiTarget, number>>> = {
	KO: { sidebarMenu: 16, tabTitle: 18 },
	EN: { sidebarMenu: 14, tabTitle: 13 },
	ZH: { sidebarMenu: 15, tabTitle: 14 },
	VI: { sidebarMenu: 14, tabTitle: 13 },
	TH: { sidebarMenu: 14, tabTitle: 13 },
};

/** selectedLanguageCd(minorCd)별 줄넘김 허용 여부 */
const LANG_TEXT_WRAP: Record<string, Partial<Record<DescUiTarget, boolean>>> = {
	EN: { sidebarMenu: true },
};

const DEFAULT_LANG_FIELD: DescLangField = "lang01";

const isDescLangField = (value?: string): value is DescLangField => {
	return !!value && /^lang(0[1-9]|1[0-5])$/.test(value);
};

/** selectedLanguageCd(minorCd) → languageList.ref01 lang 필드명 반환 */
export const getLangFieldByMinorCd = (
	languageList: SysCodeDto[],
	selectedLanguageCd: string
): DescLangField => {
	const language = languageList.find((lang) => lang.minorCd === selectedLanguageCd);
	const field = language?.ref01;

	return isDescLangField(field) ? field : DEFAULT_LANG_FIELD;
};

/** selectedLanguageCd(minorCd) + UI 영역 → 폰트 크기(px) 반환 */
export const getLangFontSizeByMinorCd = (
	selectedLanguageCd: string,
	target: DescUiTarget
): number => {
	return LANG_FONT_SIZE[selectedLanguageCd]?.[target] ?? DEFAULT_FONT_SIZE[target];
};

/** selectedLanguageCd(minorCd) + UI 영역 → 줄넘김 허용 여부 반환 */
export const getLangTextWrapByMinorCd = (
	selectedLanguageCd: string,
	target: DescUiTarget
): boolean => {
	return LANG_TEXT_WRAP[selectedLanguageCd]?.[target] ?? false;
};

/** commentCd + lang 필드로 다국어 텍스트 반환 (없으면 description → lang01 → fallback) */
//문구 조회
export const getDescText = (
	descLanguageMap: Map<string, DescLanguageDto>,
	commentCd: string,
	langField: DescLangField,
	fallback = ""
): string => {
	const desc = descLanguageMap.get(commentCd);
	if (!desc) return fallback;

	const text = desc[langField];
	if (text) return text;

	return desc.description || desc.lang01 || fallback;
};

/** `{count}` 치환자를 사용하는 건수 표시 문구를 실제 건수로 변환 */
export const formatDescCount = (count: number, template: string): string => {
	const formattedCount = count.toLocaleString();
	return template.includes("{count}")
		? template.replaceAll("{count}", formattedCount)
		: `${formattedCount}${template}`;
};
